import {
  countedBlocks,
  connectorIsLive,
  daysBetween,
  monthName,
  toDate,
  weekdayName,
  formatShortDate,
  type CalendarBlock,
  type ClassOccurrence,
  type Connector,
  type DesktopWorkerStatus,
  type ISODate,
  type Task,
} from '@app/shared'

import type { MailTriage, MailTriageItem, NotificationKind, NotificationRecord } from '../../lib/data/types'
import { localDayOf, localMinutesOfDay } from '../../lib/time'

// ============================================================================
// THE DAY SCREEN'S RULES — V10 boards 01/02 (Day), 21 (sync report), 22 (notifications) and 23
// (important mail). Pure: every sentence and number the screen shows is decided here and pinned
// by mobile/day/day.test.ts; the components only draw what these return.
//
// TIME IS 24-HOUR ON THIS SCREEN, unpadded ("9:38", "14:30") — the V10 boards draw it that way
// (owner's handoff, 2026-10-18), which is law for everything under apps/web/src/mobile/. The rest
// of the app keeps its 12-hour convention.
// ============================================================================

/** '9:38' · '14:30' — minutes past local midnight, 24-hour, unpadded hour. */
export function hm(minutes: number): string {
  const m = ((Math.floor(minutes) % 1440) + 1440) % 1440
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`
}

/** The local clock reading of an instant, 24-hour. */
export function hmOf(timestamp: string, timeZone?: string): string {
  return hm(localMinutesOfDay(timestamp, timeZone))
}

/** '6 October 2026' — the hero's date line (board 01). */
export function dayDateLine(date: ISODate): string {
  return `${toDate(date).getUTCDate()} ${monthName(date)} ${date.slice(0, 4)}`
}

/** '1h 19m' · '45m' · '2h' — how far away something is, in the hero's words. */
export function untilPhrase(minutes: number): string {
  const m = Math.max(0, Math.round(minutes))
  const h = Math.floor(m / 60)
  const r = m % 60
  if (h === 0) return `${r}m`
  return r === 0 ? `${h}h` : `${h}h ${r}m`
}

const KIND_WORD: Record<string, string> = { lecture: 'Lecture', discussion: 'Discussion', lab: 'Lab' }

// ── The next-class line (hero, bottom left) ─────────────────────────────────────────────────

export type ClassLine =
  | { kind: 'now'; eyebrow: string; code: string; line: string }
  | { kind: 'next'; eyebrow: string; code: string; line: string }
  | { kind: 'after'; eyebrow: string }
  | { kind: 'none'; eyebrow: string }

/**
 * `NOW · ENDS 12:30` while a class runs, `NEXT · IN 1H 19M` before one, and plain words after the
 * last one or on a day without any. The eyebrow is written in sentence case; the stylesheet caps it.
 */
export function classLine(classes: readonly ClassOccurrence[], now: number): ClassLine {
  const sorted = [...classes].sort((a, b) => a.startMinutes - b.startMinutes)
  const running = sorted.find((c) => c.startMinutes <= now && now < c.endMinutes)
  if (running) {
    return {
      kind: 'now',
      eyebrow: `Now · ends ${hm(running.endMinutes)}`,
      code: running.courseCode,
      line: `${hm(running.startMinutes)} – ${hm(running.endMinutes)} · ${KIND_WORD[running.kind] ?? running.kind}`,
    }
  }
  const next = sorted.find((c) => c.startMinutes > now)
  if (next) {
    return {
      kind: 'next',
      eyebrow: `Next · in ${untilPhrase(next.startMinutes - now)}`,
      code: next.courseCode,
      line: `${hm(next.startMinutes)} – ${hm(next.endMinutes)} · ${KIND_WORD[next.kind] ?? next.kind}`,
    }
  }
  if (sorted.length > 0) return { kind: 'after', eyebrow: 'No more classes today' }
  return { kind: 'none', eyebrow: 'No classes today' }
}

// ── The day's blocks (the dots row, and the free block) ─────────────────────────────────────

export interface Span {
  start: number
  end: number
}

/**
 * EVERY TIMED THING ON TODAY'S CALENDAR, ONCE. Classes, plus the calendar's accepted blocks, with
 * the same de-duplication the phone's calendar day uses (mobile/screens/MobileCalendarDay.tsx): a
 * NON-editable block whose span matches a class occurrence IS that class, mirrored from Google, and
 * is not counted twice. Suggested and cancelled blocks are not on the calendar, so not here.
 */
export function dayBlocks(
  classes: readonly ClassOccurrence[],
  blocks: readonly CalendarBlock[],
  today: ISODate,
): Span[] {
  const classSpans = new Set(classes.map((c) => `${c.startMinutes}-${c.endMinutes}`))
  const out: Span[] = classes.map((c) => ({ start: c.startMinutes, end: c.endMinutes }))
  for (const b of countedBlocks(blocks)) {
    if (b.date !== today || b.status === 'cancelled') continue
    if (!b.editable && classSpans.has(`${b.startMinutes}-${b.endMinutes}`)) continue
    out.push({ start: b.startMinutes, end: b.endMinutes })
  }
  return out.sort((a, b) => a.start - b.start || a.end - b.end)
}

export type DotKind = 'past' | 'current' | 'upcoming'

/** One dot per block: filled = over, the big ring = happening now, small ring = still to come. */
export function blockDots(spans: readonly Span[], now: number): DotKind[] {
  return spans.map((s) => (s.end <= now ? 'past' : s.start <= now ? 'current' : 'upcoming'))
}

/** The shortest gap the corner circle calls a free block (owner's brief, 2026-10-23). */
export const FREE_BLOCK_MIN = 30

export type FreeBlock =
  | { kind: 'gap'; start: number; end: number; minutes: number }
  /** Blocks still to come, but no gap of FREE_BLOCK_MIN between them. */
  | { kind: 'none'; until: number }
  /** Nothing left on today's calendar at all. */
  | { kind: 'clear' }

/** The next gap of at least 30 minutes between now and the end of the last block today. */
export function freeBlock(spans: readonly Span[], now: number, min = FREE_BLOCK_MIN): FreeBlock {
  const ahead = spans.filter((s) => s.end > now).sort((a, b) => a.start - b.start)
  if (ahead.length === 0) return { kind: 'clear' }
  let cursor = now
  for (const s of ahead) {
    if (s.start - cursor >= min) return { kind: 'gap', start: cursor, end: s.start, minutes: s.start - cursor }
    cursor = Math.max(cursor, s.end)
  }
  return { kind: 'none', until: cursor }
}

// ── Today's reminders (the reminders card, and the progress ring) ───────────────────────────

/** The day a task belongs to: its due day, else the local day its reminder rings on. */
function taskDay(t: Task): ISODate | null {
  if (t.due) return t.due
  return t.reminderAt ? localDayOf(t.reminderAt) : null
}

export interface TodaysReminders {
  /** Open, on today or overdue — overdue first, then by reminder time, untimed last. */
  open: Task[]
  /** Finished today, out of the same set: what the progress ring counts as done. */
  done: Task[]
}

/**
 * WHAT IS ON TODAY'S PLATE. Open reminders due today or earlier (or ringing today), and the ones of
 * those that were finished today. An undated reminder is not "today's", and neither is one finished
 * today that was due next week — counting either would be the ring inventing a goal.
 */
export function todaysReminders(tasks: readonly Task[], today: ISODate): TodaysReminders {
  const onPlate = (t: Task) => {
    const day = taskDay(t)
    return day !== null && day <= today
  }
  const open = tasks
    .filter((t) => !t.done && onPlate(t))
    .sort((a, b) => {
      const ao = isOverdue(a, today) ? 0 : 1
      const bo = isOverdue(b, today) ? 0 : 1
      if (ao !== bo) return ao - bo
      const at = a.reminderAt ? localMinutesOfDay(a.reminderAt) : Infinity
      const bt = b.reminderAt ? localMinutesOfDay(b.reminderAt) : Infinity
      return at - bt || a.title.localeCompare(b.title)
    })
  const done = tasks.filter(
    (t) => t.done && t.completedAt !== null && localDayOf(t.completedAt) === today && onPlate(t),
  )
  return { open, done }
}

export function isOverdue(t: Task, today: ISODate): boolean {
  const day = taskDay(t)
  return day !== null && day < today
}

/** The time a reminder row shows: its ring time today, `overdue` for one from an earlier day, or nothing. */
export function reminderTime(t: Task, today: ISODate): string | null {
  if (isOverdue(t, today)) return 'overdue'
  return t.reminderAt && localDayOf(t.reminderAt) === today ? hmOf(t.reminderAt) : null
}

/** The ring's accent share, 0–1. Null when nothing is on today's plate — "0/0" is not a ratio. */
export function progressShare(done: number, open: number): number | null {
  const total = done + open
  return total === 0 ? null : done / total
}

// ── The status chip (hero, top right) ───────────────────────────────────────────────────────

export type ChipState = 'recording' | 'notification' | 'off' | 'synced'

/** Owner, 2026-10-07: one slot, and this is who gets it. Recording > notification > off > synced. */
export function chipState(s: { recording: boolean; unread: number; online: boolean }): ChipState {
  if (s.recording) return 'recording'
  if (s.unread > 0) return 'notification'
  if (!s.online) return 'off'
  return 'synced'
}

export interface SyncSummary {
  /** The freshest `lastSyncAt` across the live connectors. Null when none has ever synced. */
  latest: string | null
  /** A live connector is in error: the chip says "As of", not "Synced". */
  stale: boolean
  live: Connector[]
}

export function syncSummary(connectors: readonly Connector[] | undefined): SyncSummary {
  const live = (connectors ?? []).filter(connectorIsLive)
  const times = live.map((c) => c.lastSyncAt).filter((t): t is string => t !== null)
  const latest = times.length > 0 ? times.reduce((a, b) => (Date.parse(a) >= Date.parse(b) ? a : b)) : null
  return { latest, stale: live.some((c) => c.status === 'error'), live }
}

// ── The sync report (board 21) ──────────────────────────────────────────────────────────────

export interface ReportRow {
  key: string
  name: string
  ok: boolean
  line: string
}

/**
 * ONE ROW PER LIVE CONNECTOR, IN ITS OWN WORDS — the same sentences the phone's sync banners have
 * always used (mobile/sync/SyncReport.tsx): the connector's own note when it is broken or throttled,
 * otherwise what its last sync brought in. Plus the PC worker, from its own status read.
 */
export function reportRows(
  connectors: readonly Connector[],
  worker: DesktopWorkerStatus | undefined,
  today?: ISODate,
): ReportRow[] {
  const rows: ReportRow[] = connectors.filter(connectorIsLive).map((c) => ({
    key: c.id,
    name: c.name,
    ok: c.status === 'ok',
    line: connectorLine(c),
  }))
  if (worker) {
    const n = worker.queue.length
    const waits = n === 0 ? 'nothing waiting' : `${n} ${n === 1 ? 'job waits' : 'jobs wait'} to transcribe`
    rows.push({
      key: 'pc-worker',
      name: 'PC worker',
      ok: worker.state === 'online',
      line:
        worker.state === 'online'
          ? `Online${worker.activity ? ` · ${worker.activity}` : ''} · ${waits}`
          : `Offline since ${sinceWhen(worker.lastSeenAt, today)} · ${waits}`,
    })
  }
  return rows
}

/** '8:10' today; 'Tue 23:48' on an earlier day — a bare clock time from yesterday reads as today's. */
export function sinceWhen(timestamp: string, today?: ISODate): string {
  if (!today || localDayOf(timestamp) === today) return hmOf(timestamp)
  const day = localDayOf(timestamp)
  const ago = daysBetween(day, today)
  return `${ago > 0 && ago < 7 ? weekdayName(day, true) : formatShortDate(day)} ${hmOf(timestamp)}`
}

/** One connector's honest line for the sync report. */
export function connectorLine(c: Connector): string {
  if (c.status === 'error') {
    const from = c.lastSyncAt ? formatShortDate(c.lastSyncAt.slice(0, 10) as ISODate) : null
    return (
      c.note?.text ??
      `${c.shortName} stopped syncing${from ? `, so ${c.outage?.stale ?? 'everything it feeds'} are from ${from}` : ''}. Nothing was lost — it just stopped updating.`
    )
  }
  if (c.status === 'rate_limited') {
    return (
      c.note?.text ??
      (c.retryAt ? `Retrying at ${hmOf(c.retryAt)}. Nothing to do.` : 'It retries on its own. Nothing to do.')
    )
  }
  if (c.lastSyncAt === null) return 'Not synced yet'
  const at = `synced ${hmOf(c.lastSyncAt)}`
  if (c.newItemsLastSync === null) return at.charAt(0).toUpperCase() + at.slice(1)
  if (c.newItemsLastSync === 0) return `No new items · ${at}`
  return `${c.newItemsLastSync} new · ${at}`
}

// ── Important mail (board 23) ───────────────────────────────────────────────────────────────

/** 'AA' from 'Academic advisor', 'NO' from notifications@…: the sender's initials, or the address's. */
export function initials(from: { name: string | null; address: string }): string {
  const name = from.name?.replace(/\(.*?\)/g, ' ').trim()
  if (name) {
    const words = name.split(/[\s·,–—-]+/).filter((w) => /[\p{L}\p{N}]/u.test(w))
    const letters = words.slice(0, 2).map((w) => [...w.replace(/[^\p{L}\p{N}]/gu, '')][0] ?? '')
    const out = letters.join('').toUpperCase()
    if (out) return out
  }
  const local = from.address.split('@')[0] ?? ''
  return ([...local.replace(/[^\p{L}\p{N}]/gu, '')].slice(0, 2).join('') || '?').toUpperCase()
}

/** When a message arrived: '7:52' today, 'Yesterday', 'Mon' this week, else 'Sep 9'. */
export function mailWhen(receivedAt: string, today: ISODate, timeZone?: string): string {
  const day = localDayOf(receivedAt, timeZone)
  const ago = daysBetween(day, today)
  if (ago <= 0) return hmOf(receivedAt, timeZone)
  if (ago === 1) return 'Yesterday'
  if (ago < 7) return weekdayName(day, true)
  return formatShortDate(day)
}

export interface MailRow {
  id: string
  from: string
  init: string
  when: string
  subject: string
  why: string | null
  snippet: string
  url: string
}

/** The sheet's rows: the "needs you today" pile, open ones only, in the Worker's order. */
export function mailRows(triage: MailTriage, today: ISODate): MailRow[] {
  return triage.groups.needsYouToday
    .filter((m) => m.state === 'open')
    .map((m: MailTriageItem) => ({
      id: m.id,
      from: m.from.name?.trim() || m.from.address,
      init: initials(m.from),
      when: mailWhen(m.receivedAt, today),
      subject: m.subject || '(no subject)',
      why: m.reason,
      snippet: m.snippet,
      url: m.url,
    }))
}

/** The freshest mail-account sync — the sheet's "Last checked". */
export function lastMailCheck(triage: MailTriage): string | null {
  const times = triage.accounts.map((a) => a.lastSyncAt).filter((t): t is string => t !== null)
  return times.length > 0 ? times.reduce((a, b) => (Date.parse(a) >= Date.parse(b) ? a : b)) : null
}

// ── Notifications (board 22) ────────────────────────────────────────────────────────────────

export type NotifIcon = 'cap' | 'bell' | 'clock' | 'test'

/**
 * Which disc a row gets. The log has four real kinds (lib/data/types.ts → NotificationKind): a class
 * starting wears the cap, a reminder the bell, a calendar block or Google event the clock. The
 * boards also draw mic and refresh discs for transcripts and sync problems; the log has no such
 * kinds, so nothing is drawn for them.
 */
export function notifIcon(kind: NotificationKind): NotifIcon {
  if (kind === 'class_start') return 'cap'
  if (kind === 'task_reminder') return 'bell'
  if (kind === 'test') return 'test'
  return 'clock'
}

/** When a row was for, as an instant: when it went out, else when it was meant to. */
export function notifAt(n: NotificationRecord): string {
  return n.sentAt ?? n.fireAt
}

/** Fired rows only, newest first, at most 20, minus the ones dismissed on this phone. */
export function visibleNotifications(rows: readonly NotificationRecord[], dismissed: ReadonlySet<string>): NotificationRecord[] {
  return rows
    .filter((n) => n.status === 'sent' && !dismissed.has(n.id))
    .sort((a, b) => Date.parse(notifAt(b)) - Date.parse(notifAt(a)))
    .slice(0, 20)
}

/** Visible rows newer than the last time the sheet was opened. */
export function unreadNotifications(visible: readonly NotificationRecord[], lastSeen: string | null): NotificationRecord[] {
  if (lastSeen === null) return [...visible]
  const seen = Date.parse(lastSeen)
  return visible.filter((n) => Date.parse(notifAt(n)) > seen)
}

export const DISMISSED_KEY = 'lifeos.mobile.notifications.dismissed'
export const SEEN_KEY = 'lifeos.mobile.notifications.seen'
/** Enough to cover the log's 20 visible rows many times over, and never grows without bound. */
const DISMISSED_CAP = 300

type KV = Pick<Storage, 'getItem' | 'setItem'>

/**
 * DISMISSALS HAVE NO SERVER STATE — they are this phone's, kept in localStorage. Every read and
 * write is wrapped: a browser that refuses storage still shows the sheet, it just forgets.
 */
export function readDismissed(storage: KV | null): Set<string> {
  try {
    const raw = storage?.getItem(DISMISSED_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [])
  } catch {
    return new Set()
  }
}

export function writeDismissed(storage: KV | null, ids: ReadonlySet<string>): void {
  try {
    storage?.setItem(DISMISSED_KEY, JSON.stringify([...ids].slice(-DISMISSED_CAP)))
  } catch {
    // Refused storage: the dismissal lasts until the app is closed.
  }
}

export function readSeen(storage: KV | null): string | null {
  try {
    return storage?.getItem(SEEN_KEY) ?? null
  } catch {
    return null
  }
}

/**
 * THE FIRST DAY ON A DEVICE COUNTS AS SEEN-AS-OF-NOW. With no marker stored yet, every notification
 * already in the log would be "new" and the chip would open on "20 new notifications" the first
 * time this ships. So an absent marker is written as `now` and returned: only what fires after the
 * phone first showed Day counts. A stored marker is returned untouched.
 */
export function seenOnFirstRun(storage: KV | null, now: string): string {
  const stored = readSeen(storage)
  if (stored !== null) return stored
  writeSeen(storage, now)
  return now
}

/** The later of two instants — "seen" only ever moves forward. */
export function laterOf(a: string | null, b: string): string {
  return a !== null && Date.parse(a) >= Date.parse(b) ? a : b
}

export function writeSeen(storage: KV | null, at: string): void {
  try {
    storage?.setItem(SEEN_KEY, at)
  } catch {
    // As above.
  }
}

/** The browser's storage, or null where touching it throws. */
export function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}
